"use client";

import { BadgeCheck } from "lucide-react";
import type { Translations } from "@/lib/translations";
import {
  getRestaurantFreePricingFeatureLines,
  getRestaurantProPricingFeatureLines,
} from "@/lib/restaurant-pricing-features";

export type RestaurantPricingCopy = Translations["restaurantsPage"]["pricing"];

type RestaurantPricingSectionProps = {
  pricing: RestaurantPricingCopy;
  onStartTrial: () => void;
};

function FeatureList({ features }: { features: string[] }) {
  return (
    <ul className="mb-6 flex-1 space-y-2">
      {features.map((feature) => (
        <li
          key={feature}
          className="flex items-start gap-2 text-xs sm:text-sm text-slate-700 dark:text-slate-200 leading-snug"
        >
          <BadgeCheck
            className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400"
            aria-hidden
          />
          {feature}
        </li>
      ))}
    </ul>
  );
}

export function RestaurantPricingSection({ pricing, onStartTrial }: RestaurantPricingSectionProps) {
  const freeFeatures = getRestaurantFreePricingFeatureLines(pricing.plans.free.features);
  const proFeatures = getRestaurantProPricingFeatureLines(pricing.plans.pro.features);

  return (
    <section className="mb-14 lg:mb-16" aria-labelledby="precio-heading">
      <div className="max-w-4xl mx-auto lg:mx-0 text-center lg:text-left mb-8">
        <h2
          id="precio-heading"
          className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-3"
        >
          {pricing.headline}
        </h2>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl mx-auto lg:mx-0">
          {pricing.supporting}
        </p>
        {pricing.tagline ? (
          <p className="mt-3 text-sm font-medium text-slate-800 dark:text-slate-200">
            {pricing.tagline}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 items-stretch gap-5 lg:gap-6 max-w-4xl mx-auto lg:mx-0">
        <article className="relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 dark:border-slate-700/60 dark:bg-slate-800/40">
          <div className="mb-5 pt-1">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {pricing.plans.free.name}
            </h3>
            <p className="mt-3 flex items-baseline gap-1">
              <span className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                {pricing.plans.free.price}
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                {pricing.plans.free.billingLabel}
              </span>
            </p>
            <p className="mt-2 text-sm font-medium text-slate-700 dark:text-slate-300 leading-snug">
              {pricing.plans.free.commitment}
            </p>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              {pricing.plans.free.tagline}
            </p>
          </div>
          <FeatureList features={freeFeatures} />
          <button
            type="button"
            onClick={onStartTrial}
            className="mt-auto w-full inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 focus:ring-offset-white dark:focus:ring-offset-slate-950"
          >
            {pricing.plans.free.cta}
          </button>
        </article>

        <article className="relative flex h-full flex-col rounded-2xl border border-emerald-500/60 bg-emerald-50/50 p-6 sm:p-7 dark:border-emerald-500/40 dark:bg-emerald-950/20 ring-1 ring-emerald-500/30">
          {pricing.plans.pro.trialBadge ? (
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
              {pricing.plans.pro.trialBadge}
            </span>
          ) : null}
          <div className="mb-5 pt-1">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {pricing.plans.pro.name}
            </h3>
            <p className="mt-3 text-sm text-slate-700 dark:text-slate-200">
              <span className="text-2xl font-bold text-slate-900 dark:text-white">
                {pricing.plans.pro.monthlyPrice}
              </span>
              <span className="text-slate-600 dark:text-slate-400">{pricing.plans.pro.monthlyLabel}</span>
              <span className="mx-2 text-slate-400">·</span>
              <span className="text-2xl font-bold text-slate-900 dark:text-white">
                {pricing.plans.pro.annualPrice}
              </span>
              <span className="text-slate-600 dark:text-slate-400">{pricing.plans.pro.annualLabel}</span>
            </p>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              {pricing.plans.pro.tagline}
            </p>
          </div>
          <FeatureList features={proFeatures} />
          <button
            type="button"
            onClick={onStartTrial}
            className="mt-auto w-full inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 focus:ring-offset-white dark:focus:ring-offset-slate-950"
          >
            {pricing.plans.pro.cta}
          </button>
        </article>
      </div>
    </section>
  );
}
